import '../styles/v2/pages/onwatch.css';
import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom';
import fetchWithTenant from '../utils/fetchWithTenant';
import {
  ArrowLeft, Loader2, FileArchive,
} from 'lucide-react';
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
} from 'recharts';

const WINDOWS = [
  { label: '24 hours', value: '24h' },
  { label: '7 days', value: '7d' },
  { label: '30 days', value: '30d' },
  { label: '90 days', value: '90d' },
];

const HEALTH_CLASS = { HEALTHY: 'healthy', WARNING: 'warning', CRITICAL: 'critical', INFORMATIONAL: 'informational', UNKNOWN: 'unknown' };

// Expected spacing between consecutive points per source, so a real gap in
// the underlying data (nothing collected, per Never False Green) can be
// told apart from ordinary point-to-point spacing. A gap wider than this
// gets an explicit null inserted so recharts breaks the line rather than
// drawing a straight line across time we have no data for. Mirrors
// onwatch.history.cjs's own step/lookback choice exactly: rollup buckets
// are always 5 minutes; Prometheus uses 15m for a 24h window, 2h otherwise
// (including the recent-week segment of a blended 30d/90d response).
function stepMsForSource(source, windowKey) {
  if (source === 'rollup') return 5 * 60000;
  if (source === 'prometheus') return windowKey === '24h' ? 15 * 60000 : 2 * 3600000;
  return null;
}

const SOURCE_LABEL = { rollup: 'rollup (5-minute buckets)', prometheus: 'Prometheus (full resolution)' };
const SOURCE_COLOR = { rollup: 'var(--ow-status-info, #3B82F6)', prometheus: 'var(--status-warn)' };

function HealthDot({ health }) {
  return <span className={`ow-dot ${HEALTH_CLASS[health] || 'unknown'} large`} title={health} />;
}

function formatUptime(seconds) {
  if (seconds == null) return 'Unknown';
  const days = Math.floor(seconds / 86400);
  const hours = Math.floor((seconds % 86400) / 3600);
  if (days > 0) return `${days}d ${hours}h`;
  const minutes = Math.floor((seconds % 3600) / 60);
  if (hours > 0) return `${hours}h ${minutes}m`;
  return `${minutes}m`;
}

function withGapBreaks(series, expectedStepMs) {
  if (!expectedStepMs || series.length < 2) return series;
  const tolerance = expectedStepMs * 1.5;
  const out = [series[0]];
  for (let i = 1; i < series.length; i++) {
    const prev = series[i - 1];
    const cur = series[i];
    if (cur.t - prev.t > tolerance) {
      out.push({ t: prev.t + expectedStepMs, value: null });
    }
    out.push(cur);
  }
  return out;
}

/**
 * A blended response's series is a straight concatenation of two
 * differently-resolved sources (onwatch.history.cjs's getBlendedRestarts);
 * this splits it back out by restarts.coverage.sources[]'s declared
 * boundaries, gap-breaks each source's own points independently (using
 * THAT source's expected step, never a single value across both), and
 * remaps each into its own dataKey (rollupValue / prometheusValue) so the
 * chart renders two visually distinct lines rather than stitching them
 * into one undifferentiated series - a support engineer looking at a
 * 90-day chart should be able to tell which part is full resolution.
 */
function buildSourcedChartData(restarts, windowKey) {
  const sources = restarts.coverage.sources || [];
  const points = restarts.series.map((p) => ({ t: new Date(p.t).getTime(), value: p.value }));

  const bySegment = sources.map((seg) => ({
    source: seg.source,
    from: new Date(seg.from).getTime(),
    to: new Date(seg.to).getTime(),
    points: [],
  }));
  for (const p of points) {
    const seg = bySegment.find((s) => p.t >= s.from && p.t <= s.to);
    if (seg) seg.points.push(p);
  }

  const merged = [];
  for (const seg of bySegment) {
    const stepMs = stepMsForSource(seg.source, windowKey);
    for (const p of withGapBreaks(seg.points, stepMs)) {
      merged.push({ t: p.t, [`${seg.source}Value`]: p.value });
    }
  }
  merged.sort((a, b) => a.t - b.t);
  return merged;
}

/**
 * "Showing N days" plus why, when coverage is partial. Distinguishes the
 * first-run case (this instance's rollup has not been running long enough
 * to reach further back, and will on its own as time passes - Task E, not
 * an error) from the general case (Task C's generic wording). Returns null
 * when coverage is complete: a working page gets no permanent banner.
 */
function buildCoverageMessage(restarts, instanceRollupFirstSeenAt) {
  const { coverage } = restarts;
  if (coverage.complete) return null;
  if (!coverage.actualFrom || !coverage.actualTo) {
    return { tone: 'informational', text: 'No history available yet for this window.' };
  }

  const spanMs = new Date(coverage.actualTo).getTime() - new Date(coverage.actualFrom).getTime();
  const days = Math.max(1, Math.round(spanMs / 86400000));
  const daysLabel = `${days} day${days === 1 ? '' : 's'}`;

  const rollupAgeMs = instanceRollupFirstSeenAt ? Date.now() - new Date(instanceRollupFirstSeenAt).getTime() : null;
  const isFirstRun = rollupAgeMs != null && rollupAgeMs < 86400000;

  if (isFirstRun) {
    return {
      tone: 'informational',
      text: `Long-range history is still accumulating. ${daysLabel} of live data shown below.`,
    };
  }

  const accumulatesFrom = instanceRollupFirstSeenAt ? new Date(instanceRollupFirstSeenAt).toLocaleDateString() : null;
  return {
    tone: 'informational',
    text: `Showing ${daysLabel}.${accumulatesFrom ? ` History beyond that begins accumulating from ${accumulatesFrom}.` : ''}`,
  };
}

export default function OnWatchWorkloadDetail() {
  const { id, namespace, workloadName } = useParams();
  const [searchParams] = useSearchParams();
  const kind = searchParams.get('kind');
  const navigate = useNavigate();

  const [windowKey, setWindowKey] = useState('24h');
  const [history, setHistory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchHistory = useCallback(async () => {
    setLoading(true);
    try {
      const qs = new URLSearchParams({ window: windowKey, ...(kind ? { kind } : {}) });
      const res = await fetchWithTenant(
        `/api/onwatch/instances/${id}/workloads/${encodeURIComponent(namespace)}/${encodeURIComponent(workloadName)}/history?${qs.toString()}`
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setHistory(await res.json());
      setError(null);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [id, namespace, workloadName, windowKey, kind]);

  useEffect(() => { fetchHistory(); }, [fetchHistory]);

  if (loading && !history) {
    return (
      <div className="page-content">
        <div className="md-loading"><Loader2 size={32} className="spin" /><p>Loading workload history...</p></div>
      </div>
    );
  }

  if (error && !history) {
    return (
      <div className="page-content">
        <div className="ow-form-error">{error}</div>
        <Link to={`/monitoring/onwatch/${id}`}><ArrowLeft size={14} /> Back to cluster health</Link>
      </div>
    );
  }

  const { current, restarts, healthTimeline, pods, instanceRollupFirstSeenAt } = history;
  const chartData = buildSourcedChartData(restarts, windowKey);
  const coverageMessage = buildCoverageMessage(restarts, instanceRollupFirstSeenAt);

  // Task B: clamp the axis to what we actually have, not the requested
  // window - drawing 83 empty days with repeating labels reads as broken,
  // not as an honest report of thin data. Falls back to recharts' own
  // dataMin/dataMax only when there is truly nothing to clamp to.
  const actualFromMs = restarts.coverage.actualFrom ? new Date(restarts.coverage.actualFrom).getTime() : null;
  const actualToMs = restarts.coverage.actualTo ? new Date(restarts.coverage.actualTo).getTime() : null;
  const axisDomain = actualFromMs != null && actualToMs != null ? [actualFromMs, actualToMs] : ['dataMin', 'dataMax'];

  // Date-only tick labels collapse to the same string when the whole
  // visible span sits inside one calendar day - part of what made a
  // few-hours-old rollup look like a frozen 90-day chart. Time-of-day is
  // added once the span is under two days.
  const domainSpanMs = actualFromMs != null && actualToMs != null ? actualToMs - actualFromMs : null;
  const tickFormatter = domainSpanMs != null && domainSpanMs < 2 * 86400000
    ? (t) => new Date(t).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    : (t) => new Date(t).toLocaleDateString();

  // Task D: the chart's own window total, computed from the same points
  // being drawn, so it is never a claim independent of what the user is
  // looking at. Distinct from a pod's lifetime restartCount below.
  const windowedRestartTotal = restarts.series.reduce((sum, p) => sum + (p.value ?? 0), 0);
  const windowLabel = WINDOWS.find((w) => w.value === windowKey)?.label || windowKey;

  return (
    <div className="page-content">
      <Link to={`/monitoring/onwatch/${id}`} style={{ fontSize: 12, color: 'var(--text-tertiary)', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
        <ArrowLeft size={12} /> Cluster health
      </Link>

      <div className="page-header-with-actions">
        <div>
          <h1 className="page-title">
            <HealthDot health={current.health} /> {history.workloadName}
          </h1>
          <div className="ow-workload-detail-meta">
            {history.kind} in {history.namespace} · Uptime {formatUptime(current.uptimeSeconds)}
            {current.replicasDesired != null && ` · ${current.replicasReady ?? 0}/${current.replicasDesired} ready`}
          </div>
        </div>
        <div className="ow-controls">
          <button
            className="btn btn-secondary btn-sm"
            onClick={() => navigate(`/monitoring/onwatch/${id}?collectLogs=${encodeURIComponent(history.workloadName)}`)}
          >
            <FileArchive size={14} /> Collect logs
          </button>
        </div>
      </div>

      {current.lastTerminatedReason && current.lastTerminatedReason !== 'Completed' && (
        <div className="ow-workload-terminated-banner">
          Last terminated: <strong>{current.lastTerminatedReason}</strong>
          {current.lastTerminatedAt && ` at ${new Date(current.lastTerminatedAt).toLocaleString()}`}
        </div>
      )}

      <div className="ow-wizard-section">
        <div className="ow-chip-row">
          {WINDOWS.map((w) => (
            <button key={w.value} className={`ow-chip${windowKey === w.value ? ' active' : ''}`} onClick={() => setWindowKey(w.value)}>{w.label}</button>
          ))}
        </div>
        {coverageMessage && (
          <div className="ow-coverage-message informational">{coverageMessage.text}</div>
        )}
      </div>

      <div className="ow-node-card">
        <div className="ow-node-header">
          <span className="ow-node-name">Restarts</span>
          <span className="ow-history-source">
            source: {restarts.source}{!restarts.coverage.complete && ' · reduced coverage'}
          </span>
        </div>
        <div className="ow-workload-detail-meta" style={{ marginBottom: 8 }}>
          {windowedRestartTotal} in the last {windowLabel.toLowerCase()}
          {restarts.coverage.sources && restarts.coverage.sources.length > 1 && (
            <>
              {' · '}
              {restarts.coverage.sources.map((s, i) => (
                <span key={i} className="ow-history-legend-item">
                  <span className="ow-history-legend-dot" style={{ background: SOURCE_COLOR[s.source] }} />
                  {SOURCE_LABEL[s.source] || s.source}
                  {i < restarts.coverage.sources.length - 1 ? ', ' : ''}
                </span>
              ))}
            </>
          )}
        </div>
        <ResponsiveContainer width="100%" height={220}>
          <LineChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
            <XAxis dataKey="t" type="number" domain={axisDomain} tickFormatter={tickFormatter} />
            <YAxis allowDecimals={false} />
            <Tooltip labelFormatter={(t) => new Date(t).toLocaleString()} />
            <Line type="monotone" dataKey="rollupValue" name={SOURCE_LABEL.rollup} stroke={SOURCE_COLOR.rollup} dot={false} connectNulls={false} isAnimationActive={false} />
            <Line type="monotone" dataKey="prometheusValue" name={SOURCE_LABEL.prometheus} stroke={SOURCE_COLOR.prometheus} dot={false} connectNulls={false} isAnimationActive={false} />
          </LineChart>
        </ResponsiveContainer>
        {restarts.note && <div className="ow-node-stale-footer">{restarts.note}</div>}
      </div>

      <div className="ow-node-card">
        <div className="ow-node-header"><span className="ow-node-name">Current pods</span></div>
        {pods.available ? (
          <div className="ow-history-pods">
            {pods.pods.length === 0 && <div className="ow-form-help">No live pods for this workload right now.</div>}
            {pods.pods.map((p) => (
              <div key={p.podName} className="ow-history-pod-row">
                <span className="ow-workload-name">{p.podName}</span>
                <span className="ow-kind-badge">{p.phase}</span>
                {/* Task D: labelled explicitly as a lifetime total, not a
                    figure from the charted window above - it comes from
                    the pod's own live counter, not the rollup or
                    Prometheus, and sitting under a near-flat chart it used
                    to read as a contradiction rather than a different
                    measure. */}
                <span className="ow-workload-fraction">{p.restartCount} restart{p.restartCount === 1 ? '' : 's'} total</span>
                <span className="ow-form-help">since pod start {p.startTime ? new Date(p.startTime).toLocaleString() : 'unknown'}</span>
              </div>
            ))}
          </div>
        ) : (
          <div className="ow-form-help">{pods.error || 'Live pod data unavailable right now.'}</div>
        )}
      </div>

      {healthTimeline.transitions.length > 0 && (
        <div className="ow-node-card">
          <div className="ow-node-header">
            <span className="ow-node-name">Health transitions</span>
            {!healthTimeline.coverage.complete && <span className="ow-history-source">reduced coverage</span>}
          </div>
          <div className="ow-history-timeline">
            {healthTimeline.transitions.map((t) => (
              <div key={t.at} className="ow-history-timeline-row">
                <HealthDot health={t.health} />
                <span>{t.health}</span>
                <span className="ow-form-help">{new Date(t.at).toLocaleString()}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
